import { action, computed, makeObservable, observable } from "mobx";
import type React from "react";
import i18n from "@/modules/common/i18n";
import ApiService from "../../common/InvApiService";
import type { URL } from "../../util/types";
import type {
  BarcodeRecord,
  Deleted,
  FromServer,
  GeneratedBarcodeAttrs,
  PersistedBarcodeAttrs,
} from "../definitions/Barcode";
import type { Id } from "../definitions/BaseRecord";

function fetchImage(url: URL, description: string): Promise<File> {
  return ApiService.query<Blob>(url, new URLSearchParams(), true).then(({ data }) => {
    return new File([data], description, { type: "image/png" });
  });
}

/*
 * This class models, for the purposes of displaying in the UI, any barcode
 * stored in the Inventory system; either generated by us or having been read
 * from some existing barcode in a lab.
 */
export class PersistedBarcode implements BarcodeRecord {
  id: Id;
  data: string;
  description: string;
  newBarcodeRequest: boolean;
  deleteBarcodeRequest: boolean;
  imageUrl: URL | null;
  image: File | null;

  constructor(attrs: PersistedBarcodeAttrs) {
    makeObservable(this, {
      id: observable,
      data: observable,
      description: observable,
      imageUrl: observable,
      image: observable,
      markAsDeleted: action,
      setDescription: action,
      paramsForBackend: computed,
      isDeleted: computed,
    });
    // @ts-expect-error just the NewlyCreated attrs
    if (attrs.newBarcodeRequest) {
      this.id = null;
      this.newBarcodeRequest = true;
      this.deleteBarcodeRequest = false;
      this.description = attrs.description;
      this.imageUrl = null;
      this.image = null;
      // @ts-expect-error just the Deleted attrs
    } else if (attrs.deleteBarcodeRequest) {
      const deletedAttrs = attrs as Deleted;
      this.id = deletedAttrs.id;
      this.newBarcodeRequest = false;
      this.deleteBarcodeRequest = true;
      this.description = attrs.description;
      this.imageUrl = deletedAttrs.imageUrl;
      this.image = null;
    } else {
      const fromServerAttrs = attrs as FromServer;
      this.id = fromServerAttrs.id;
      this.newBarcodeRequest = false;
      this.deleteBarcodeRequest = false;
      this.description = attrs.description;

      this.image = null;
      const imageUrl = fromServerAttrs._links.find(({ rel }) => rel === "enclosure")?.link ?? null;
      this.imageUrl = imageUrl;
    }
    this.data = attrs.data;
  }

  fetchImage(): Promise<File> {
    if (!this.imageUrl) return Promise.reject();
    return fetchImage(this.imageUrl, this.description);
  }

  markAsDeleted(): void {
    this.deleteBarcodeRequest = true;
  }

  deletedCopy(): BarcodeRecord | null {
    if (this.id === null) return null;
    return new PersistedBarcode({
      id: this.id,
      deleteBarcodeRequest: true,
      data: this.data,
      description: this.description,
      imageUrl: this.imageUrl,
    });
  }

  get paramsForBackend(): Record<string, unknown> {
    const params: {
      newBarcodeRequest?: true;
      deleteBarcodeRequest?: true;
      id: Id;
      data: string;
      description: string;
    } = {
      id: this.id,
      data: this.data,
      description: this.description,
    };
    if (this.newBarcodeRequest) params.newBarcodeRequest = true;
    if (this.deleteBarcodeRequest) params.deleteBarcodeRequest = true;
    return params;
  }

  setDescription(value: string): void {
    this.description = value;
  }

  get isDeleted(): boolean {
    return this.deleteBarcodeRequest;
  }

  get isDeletable(): boolean {
    return true;
  }

  get descriptionIsEditable(): boolean {
    return true;
  }

  get renderedDescription(): React.ReactNode {
    return <>{this.description}</>;
  }
}

export class GeneratedBarcode implements BarcodeRecord {
  data: string;
  description: string;
  imageUrl: URL | null;

  constructor(attrs: GeneratedBarcodeAttrs) {
    makeObservable(this, {
      data: observable,
      description: observable,
      imageUrl: observable,
      paramsForBackend: computed,
      isDeleted: computed,
    });
    this.data = attrs.data;
    this.description = i18n.t("inventory:recordInfo.barcodes.model.generatedDescription", { data: this.data });
    this.imageUrl = `barcodes?content=${this.data}&barcodeType=QR`;
  }

  fetchImage(): Promise<File> {
    if (!this.imageUrl) return Promise.reject();
    return fetchImage(this.imageUrl, this.description);
  }

  markAsDeleted(): void {
    throw new Error("Cannot deleted generated barcodes");
  }

  deletedCopy(): BarcodeRecord | null {
    throw new Error("Cannot delete generated barcodes");
  }

  get paramsForBackend(): Record<string, unknown> {
    throw new Error("Cannot serialise generated barcodes");
  }

  setDescription(_value: string): void {
    throw new Error("Cannot modify the description of a generated barcode");
  }

  get isDeleted(): boolean {
    return false;
  }

  get isDeletable(): boolean {
    return false;
  }

  get descriptionIsEditable(): boolean {
    return false;
  }

  get renderedDescription(): React.ReactNode {
    return (
      <>
        {i18n.t("inventory:recordInfo.barcodes.model.generatedByRspace")}
        <br />
        {this.data}
      </>
    );
  }
}
